import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { ApiError } from "../lib/api";
import {
  ADD_PROJECT_SHEET_TITLE,
  COULD_NOT_SAVE_ERROR,
  editProjectSheetTitle,
  projectNameConflictMessage,
  PROJECT_NAME_FIELD_LABEL,
  PROJECT_STATUS_FIELD_LABEL,
  PROJECT_STATUS_SUGGESTIONS,
  PROJECT_SUMMARY_FIELD_LABEL,
  SAVE_CHANGES_LABEL,
} from "../lib/recordsCopy";
import type { ProjectOut } from "../types";

export interface ProjectFormSheetFields {
  name: string;
  status: string | null;
  summary: string | null;
}

export interface ProjectFormSheetProps {
  open: boolean;
  /** `"add"` (default) — the `ProjectsView` header's `"+ Add"` sheet.
   * `"edit"` — `ProjectDetailOverlay`'s admin-only "✎ Edit" affordance,
   * pre-filled from `project`. Same single-sheet, mode-driven shape as
   * `AnimalFormSheet` (design-spec-animal-cards-2026-07-10.md §5's own
   * "the form-sheet pattern is binding for ALL three entities' CRUD UI"
   * instruction) — this replaces the old summary/status-only
   * `EditProjectSheet`, widened to also cover `name` (sprint-013 C2, SA
   * ruling Decision 2.5). */
  mode?: "add" | "edit";
  /** Required when `mode === "edit"` — the project being edited, to
   * pre-fill every field. */
  project?: ProjectOut;
  onAdd?: (fields: ProjectFormSheetFields) => Promise<void>;
  onSave?: (fields: ProjectFormSheetFields) => Promise<void>;
  onCancel: () => void;
}

/**
 * Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5) — the
 * Projects Add/Edit bottom sheet: name (required), status (suggested-
 * chips + free text, ADR-002 A6-R6 ratified — `projects.status` has no
 * server enum), summary (optional). Same chrome/dismiss conventions as
 * every other sheet in this app and the same synchronous-reset-on-open
 * discipline as `AnimalFormSheet`/`AddPersonSheet`.
 *
 * **Status field — suggested chips, free text still accepted** (carried
 * forward unchanged from the old `EditProjectSheet`, which this component
 * replaces): a plain text input, pre-filled with the project's current RAW
 * status on edit (never silently rewritten to a label), plus a row of the
 * eight governed labels as tap-to-fill suggestions. Tapping a chip does
 * not lock the field.
 */
export function ProjectFormSheet({
  open,
  mode = "add",
  project,
  onAdd,
  onSave,
  onCancel,
}: ProjectFormSheetProps) {
  const [name, setName] = useState(mode === "edit" ? (project?.name ?? "") : "");
  const [status, setStatus] = useState(mode === "edit" ? (project?.status ?? "") : "");
  const [summary, setSummary] = useState(mode === "edit" ? (project?.summary ?? "") : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset SYNCHRONOUSLY on the closed->open transition — see
  // `AnimalFormSheet.tsx`'s docstring (itself citing `AddPersonSheet.tsx`)
  // for why this must never be a post-commit `useEffect`.
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setName(mode === "edit" ? (project?.name ?? "") : "");
    setStatus(mode === "edit" ? (project?.status ?? "") : "");
    setSummary(mode === "edit" ? (project?.summary ?? "") : "");
    setError(null);
  }
  wasOpenRef.current = open;

  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: nameRef,
  });

  if (!open) return null;

  const trimmedName = name.trim();

  async function submit() {
    if (!trimmedName) return;
    setSaving(true);
    setError(null);
    const fields: ProjectFormSheetFields = {
      name: trimmedName,
      status: status.trim() ? status.trim() : null,
      summary: summary.trim() ? summary.trim() : null,
    };
    try {
      if (mode === "edit") {
        await onSave?.(fields);
      } else {
        await onAdd?.(fields);
      }
    } catch (err) {
      // design-spec §5's "Inline validation / error states" — the
      // specific 409 name-conflict message, never the generic fallback,
      // for this one case (projects share the same unique-name-index
      // posture as animals).
      if (err instanceof ApiError && err.status === 409) {
        setError(projectNameConflictMessage(trimmedName));
      } else {
        setError(err instanceof Error ? err.message : COULD_NOT_SAVE_ERROR);
      }
    } finally {
      setSaving(false);
    }
  }

  const titleId = "project-form-sheet-title";

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id={titleId}>
          {mode === "edit" ? editProjectSheetTitle(project?.name ?? "") : ADD_PROJECT_SHEET_TITLE}
        </h2>

        <label class="admin-field-label" for="project-form-name" style={{ marginTop: 0 }}>
          {PROJECT_NAME_FIELD_LABEL}
        </label>
        <input
          ref={nameRef}
          id="project-form-name"
          type="text"
          class="pick-input"
          value={name}
          required
          aria-required="true"
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />

        <label class="admin-field-label" for="project-form-status">
          {PROJECT_STATUS_FIELD_LABEL}
        </label>
        <input
          id="project-form-status"
          type="text"
          class="pick-input"
          value={status}
          onInput={(e) => setStatus((e.target as HTMLInputElement).value)}
        />
        <div class="admin-day-picker" role="group" aria-label="Suggested status">
          {PROJECT_STATUS_SUGGESTIONS.map((label) => {
            const isSelected = status === label;
            return (
              <button
                key={label}
                type="button"
                class={`admin-day-chip${isSelected ? " is-selected" : ""}`}
                aria-pressed={isSelected}
                onClick={() => setStatus(label)}
              >
                {label}
              </button>
            );
          })}
        </div>

        <label class="admin-field-label" for="project-form-summary">
          {PROJECT_SUMMARY_FIELD_LABEL}
        </label>
        <textarea
          id="project-form-summary"
          class="pick-input admin-textarea"
          value={summary}
          onInput={(e) => setSummary((e.target as HTMLTextAreaElement).value)}
        />

        {error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          class="primary-btn"
          style={{ marginTop: "14px" }}
          disabled={!trimmedName || saving}
          onClick={() => void submit()}
        >
          {saving ? "Saving…" : mode === "edit" ? SAVE_CHANGES_LABEL : "Add"}
        </button>
        <button type="button" class="secondary-btn" style={{ marginTop: "10px" }} onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
